import { useState, useRef, useEffect, useCallback } from 'react'
import { format } from 'date-fns'
import { Button, Spinner, Badge, TextArea, RadioGroup } from '@/components/ui'
import { api, ApiError } from '@/services/api'
import { getErrorMessage } from '@/services/errors'
import { ErrorCode } from '@/services/types'
import type { ConnectedAccount, VoiceMode } from '@/services/types'

const VOICE_MODE_OPTIONS = [
  { value: 'automated', label: 'Automated' },
  { value: 'manual', label: 'Manual' },
  { value: 'off', label: 'Off' },
]

interface AccountVoiceConfigProps {
  account: ConnectedAccount
  onUpdate: () => void
  addToast: (message: string, type: 'success' | 'error' | 'info') => void
}

export function AccountVoiceConfig({ account, onUpdate, addToast }: AccountVoiceConfigProps) {
  const { voice_config } = account
  const [mode, setMode] = useState<VoiceMode>(voice_config.mode)
  const [manualDescription, setManualDescription] = useState(voice_config.manual_description ?? '')
  const [isSavingManual, setIsSavingManual] = useState(false)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisProgress, setAnalysisProgress] = useState<number | undefined>()
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [])

  const handleModeChange = useCallback(async (newMode: string) => {
    const voiceMode = newMode as VoiceMode
    setMode(voiceMode)
    try {
      await api.updateAccountVoiceConfig(account.id, { mode: voiceMode })
      onUpdate()
    } catch (err) {
      addToast(
        err instanceof ApiError ? getErrorMessage(err, 'Failed to update voice mode') : 'Failed to update voice mode',
        'error'
      )
      setMode(voice_config.mode) // revert
    }
  }, [account.id, voice_config.mode, onUpdate, addToast])

  const handleSaveManual = async () => {
    setIsSavingManual(true)
    try {
      await api.updateAccountVoiceConfig(account.id, {
        mode: 'manual',
        manual_description: manualDescription,
      })
      addToast('Voice description saved', 'success')
      onUpdate()
    } catch (err) {
      addToast(
        err instanceof ApiError ? getErrorMessage(err, 'Failed to save description') : 'Failed to save description',
        'error'
      )
    } finally {
      setIsSavingManual(false)
    }
  }

  const startAnalysis = async () => {
    setIsAnalyzing(true)
    setAnalysisProgress(undefined)

    try {
      const response = await api.analyzeAccountVoice(account.id)

      if (response.status === 'completed') {
        addToast('Voice analysis complete!', 'success')
        setIsAnalyzing(false)
        onUpdate()
        return
      }

      if (response.job_id) {
        pollingRef.current = setInterval(async () => {
          try {
            const statusResponse = await api.getAccountVoiceAnalysisStatus(account.id, response.job_id!)
            setAnalysisProgress(statusResponse.progress)

            if (statusResponse.status === 'completed') {
              if (pollingRef.current) clearInterval(pollingRef.current)
              addToast('Voice analysis complete!', 'success')
              setIsAnalyzing(false)
              onUpdate()
            } else if (statusResponse.status === 'failed') {
              if (pollingRef.current) clearInterval(pollingRef.current)
              addToast('Voice analysis failed. Please try again.', 'error')
              setIsAnalyzing(false)
            }
          } catch (err) {
            console.error('Failed to check analysis status:', err)
          }
        }, 2000)
      }
    } catch (err) {
      setIsAnalyzing(false)
      if (err instanceof ApiError) {
        if (err.code === ErrorCode.VOICE_ANALYSIS_IN_PROGRESS) {
          addToast('Voice analysis is already in progress', 'info')
        } else if (err.code === ErrorCode.INSUFFICIENT_TWEETS) {
          addToast('Not enough posts to analyze. Please post more content on X first.', 'error')
        } else {
          addToast(getErrorMessage(err, 'Failed to start voice analysis'), 'error')
        }
      } else {
        addToast('Failed to start voice analysis', 'error')
      }
    }
  }

  const voiceProfile = voice_config.voice_profile

  return (
    <div className="mt-4 pt-4 border-t border-border">
      <h4 className="text-sm font-medium mb-3">Voice Configuration</h4>

      <RadioGroup
        name={`voice-mode-${account.id}`}
        options={VOICE_MODE_OPTIONS}
        value={mode}
        onChange={handleModeChange}
      />

      <div className="mt-4">
        {mode === 'automated' && (
          <>
            {isAnalyzing ? (
              <div className="flex items-center gap-3">
                <Spinner size="sm" />
                <p className="text-sm text-secondary-text">
                  Analyzing voice{analysisProgress != null ? ` (${analysisProgress}%)` : '...'}
                </p>
              </div>
            ) : voiceProfile ? (
              <div className="space-y-3">
                <p className="text-sm text-secondary-text">
                  {voiceProfile.analysis_data.analysis_summary}
                </p>
                {voiceProfile.analysis_data.common_topics.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {voiceProfile.analysis_data.common_topics.map((topic) => (
                      <Badge key={topic} variant="outline">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-secondary-text">
                  Based on {voiceProfile.analysis_data.sample_size} posts
                </p>
                <div className="flex items-center gap-3">
                  <Button variant="secondary" onClick={startAnalysis} loading={isAnalyzing}>
                    Re-analyze
                  </Button>
                  <span className="text-xs text-secondary-text">
                    Last updated {format(new Date(voiceProfile.updated_at), 'MMM d, yyyy')}
                  </span>
                </div>
              </div>
            ) : (
              <Button onClick={startAnalysis} loading={isAnalyzing}>
                Analyze My Voice
              </Button>
            )}
          </>
        )}

        {mode === 'manual' && (
          <div className="space-y-3">
            <TextArea
              placeholder="Describe your writing style..."
              value={manualDescription}
              onChange={(e) => setManualDescription(e.target.value)}
              rows={3}
            />
            <Button
              variant="secondary"
              onClick={handleSaveManual}
              loading={isSavingManual}
              disabled={!manualDescription.trim()}
            >
              Save
            </Button>
          </div>
        )}

        {mode === 'off' && (
          <p className="text-sm text-secondary-text">
            Voice matching is disabled for this account.
          </p>
        )}
      </div>
    </div>
  )
}
